import { User } from './user.model';

export class Terminal {
    public id: string;
    public identifier: string;
    public serialNumber: string;
    public contractStartedAt?: string;
    public contractDurationMonths?: number;
    public contractType?: string;
    public implantedAt?: string;
    public storeChain?: string;
    public isAvailable: boolean | null;
    public isCartridgeStuck: boolean;
    public isBoxFull: boolean;
    public paperLvl: number | null;
    public hasPrerequisites: boolean;
    public lastStatusUpdated?: string;
    public currentYearRank: number | null;
    public createdAt?: string;
    public commercialId?: number;
    public commercial?: User;
    public user?: User;

    constructor(
        id: string,
        serialNumber: string,
        identifier: string,
        contractStartedAt?: string,
        contractDurationMonths?: number,
        contractType?: string,
        implantedAt?: string,
        storeChain?: string,
        isAvailable: boolean | null = null,
        isCartridgeStuck: boolean = false,
        isBoxFull: boolean = false,
        paperLvl: number | null = null,
        hasPrerequisites: boolean = true,
        lastStatusUpdated?: string,
        currentYearRank?: number | null,
        createdAt?: string,
        commercialId?: number,
        commercial?: User,
        user?: User
    ) {
        this.id = id;
        this.serialNumber = serialNumber;
        this.identifier = identifier;
        this.contractStartedAt = contractStartedAt;
        this.contractDurationMonths = contractDurationMonths;
        this.contractType = contractType;
        this.implantedAt = implantedAt;
        this.storeChain = storeChain;
        this.isAvailable = isAvailable ?? null;
        this.isCartridgeStuck = isCartridgeStuck;
        this.isBoxFull = isBoxFull;
        this.paperLvl = paperLvl ?? null;
        this.hasPrerequisites = hasPrerequisites;
        this.lastStatusUpdated = lastStatusUpdated;
        this.currentYearRank = currentYearRank ?? null;
        this.createdAt = createdAt;
        this.commercialId = commercialId;
        this.commercial = commercial;
        this.user = user;
    }

    /**
     * Retourne une date formatée pour l'affichage
     */
    public getFormattedContractDate(): string {
        if (!this.contractStartedAt) return 'Non défini';
        return new Date(this.contractStartedAt).toLocaleDateString('fr-FR');
    }

    /**
     * Calcule et retourne la date de fin de contrat formatée
     */
    public getFormattedContractEndDate(): string {
        if (!this.contractStartedAt || !this.contractDurationMonths) return 'Non défini';

        const startDate = new Date(this.contractStartedAt);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + this.contractDurationMonths);

        return endDate.toLocaleDateString('fr-FR');
    }

    /**
     * Retourne la date d'implantation formatée
     */
    public getFormattedImplantationDate(): string {
        const implantationSource = this.implantedAt ?? this.contractStartedAt;
        if (!implantationSource) return 'Non défini';
        return new Date(implantationSource).toLocaleDateString('fr-FR');
    }

    /**
     * Retourne le type de contrat formaté
     */
    public getFormattedContractType(): string {
        if (!this.contractType) return 'Non défini';
        return this.contractType === 'LOCATION' ? 'Location' : 'Vente';
    }

    /**
     * Retourne une date formatée pour la création
     */
    public getFormattedCreationDate(): string {
        if (!this.createdAt) return 'Non défini';
        return new Date(this.createdAt).toLocaleDateString('fr-FR');
    }

    /**
     * Retourne le statut avec une couleur appropriée
     */
    public getStatusColor(): string {
        if (this.isAvailable === true) {
            return 'success';
        }

        if (this.isAvailable === false) {
            return 'danger';
        }

        return 'warning';
    }

    public getStatusLabel(): string {
        if (this.isAvailable === true) {
            return 'Disponible';
        }

        if (this.isAvailable === null) {
            return 'Inconnu';
        }

        if (!this.hasPrerequisites) {
            return 'Pré-requis manquants';
        }

        if (this.isCartridgeStuck) {
            return 'Cartouche bloquée';
        }

        if (this.isBoxFull) {
            return 'Borne pleine';
        }

        if (this.paperLvl != null && this.paperLvl <= 10) {
            return 'Niveau papier faible';
        }
        return '';

    }

    public getAvailabilityLabel(): string {
        //dashboard
        if (this.isAvailable === true) {
            return 'Disponible';
        }

        if (this.isAvailable === false) {
            return 'Indisponible';
        }

        return 'Inconnu';
    }

    public getUnavailableReason(): string | null {
        //dashboard
        if (this.isAvailable !== false) {
            return null;
        }

        if (this.isCartridgeStuck) {
            return 'Cartouche bloquée';
        }

        if (this.isBoxFull) {
            return 'Borne pleine';
        }

        if (!this.hasPrerequisites) {
            return 'Pré-requis manquants';
        }

        if (this.paperLvl != null && this.paperLvl <= 0) {
            return 'Plus de papier';
        }

        return null;
    }

    public getTerminalType(): 'wisee-one' | 'wisee-mini' {
        const normalized = this.identifier?.toUpperCase() ?? '';

        if (normalized.startsWith('WM')) {
            return 'wisee-mini';
        }

        if (normalized.startsWith('B')) {
            return 'wisee-one';
        }

        return 'wisee-one';
    }

    public getTerminalTypeLabel(): string {
        return this.getTerminalType() === 'wisee-mini' ? 'Wisee Mini' : 'Wisee';
    }
}
